import { Location } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AgBarSeriesOptions, AgBarSeriesTooltipRendererParams } from "ag-charts-community";
import { ColDef } from "@ag-grid-community/core";
import { Manufacturer } from "app/model/entities/entity-model";
import { ProductTypeCountInInventory } from "app/model/entities/-chart-classes";

import { DbQueryService } from "app/shared/db-query.service";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

@Component({
  selector: "app-chart-product-type-inventory",
  templateUrl: "./chart-product-type-inventory.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ChartProductTypeInventoryComponent extends DomainBaseComponent implements OnInit {
  title: string;
  options: any;
  private chartData: ProductTypeCountInInventory[] = [];

  manufacturers: Manufacturer[] = [];
  selectedManufacturer: Manufacturer | null = null;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);
  }

  async ngOnInit() {
    // Load manufacturers for dropdown
    const allManufs = await this.uow.queryAll(Manufacturer, "Manufacturers");
    this.manufacturers = _.sortBy(allManufs, (m) => m.name);

    this.isPageReady = true;
  }

  async onManufacturerChange() {
    if (!this.selectedManufacturer) {
      this.chartData = [];
      this.options = null;
      return;
    }

    await this.processChartData();
  }

  private async processChartData() {
    if (!this.selectedManufacturer) return;

    try {
      const data = await this.dbQueryService.getTopProductTypesInInventoryForManufacturer(this.selectedManufacturer.id);
      console.log("Product Type Inventory data:", data);
      this.chartData = data || [];

      this.title = this.getTitle();

      if (this.chartData.length === 0) {
        this.options = null;
        return;
      }

      this.options = {
        title: {
          text: this.title,
        },
        data: this.chartData,
        series: [
          {
            type: "bar",
            xKey: "productTypeStyle",
            yKey: "count",
            yName: "Inventory Count",
            tooltip: { renderer: this.renderer.bind(this) },
          } as AgBarSeriesOptions,
        ],
      };
    } catch (error) {
      console.error("Error loading product type inventory data:", error);
      this.chartData = [];
      this.options = null;
    }
  }

  getTitle() {
    const manufName = this.selectedManufacturer?.name ?? "All";
    return `Top 10 Product Types - ${manufName}`;
  }

  renderer(params: AgBarSeriesTooltipRendererParams<ProductTypeCountInInventory>) {
    return {
      title: params.datum.productTypeStyle,
      content: `${params.datum.productTypeDescription}<br/>${params.datum.count} Items`,
    };
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Product Type Inventory");
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:C1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:C2");

    worksheet.addRow([]);

    worksheet.addRow(["Style", "Description", "Item Count"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    worksheet.getColumn(1).width = 20;
    worksheet.getColumn(2).width = 40;
    worksheet.getColumn(3).width = 15;

    this.chartData.forEach((item) => {
      worksheet.addRow([item.productTypeStyle, item.productTypeDescription, item.count]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Product_Type_Inventory_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): ProductTypeCountInInventory[] {
    return this.chartData;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "productTypeStyle", headerName: "Style", flex: 1 },
      { field: "productTypeDescription", headerName: "Description", flex: 2 },
      { field: "count", headerName: "Item Count", flex: 1 },
    ];
  }

  getExportFileName(): string {
    return "Product_Type_Inventory";
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
